import React from "react";

import { IPanelItem, PanelItem } from "@components/panels/PanelItems";
import { ExpandablePanelItem } from "@components/panels/ExpandablePanelItems";
import Label from "@components/inputs/text/Label";
import {
  IPricingRecord,
  IPricingRecordSection,
} from "@services/QuoteBuilder/QuoteBuilderService";
import { getTotalWeeklySubscriptionPrice } from "@utils/pricingRecordHelpers";
import { Country } from "@utils/constants/localisation";
import { countrySpecificCurrency } from "@utils/misc/functions";

type Props = {
  pricingRecord: any;
  showTotalWeeklySubscriptionPrice?: boolean;
  showSetupFee?: boolean;
  defaultExpanded?: boolean;
  foQuoting21Enabled?: boolean;
};

/**
 * Handler for grouping pricing data so they are shown a specific way
 */
const formatPriceBreakdown = (
  pricingRecord: IPricingRecord,
  sectionId: Number
) => {
  // Filter for included items only in this section
  const sectionDetails = pricingRecord.pricing_record_line_items.filter(
    (item) => item.pricing_template_section_id == sectionId && item.included
  );
  const sectionIncludingGstTotal = sectionDetails.reduce(
    (sum, item) => sum + item.price_including_gst,
    0
  );
  const sectionExcludingGstTotal = sectionDetails.reduce(
    (sum, item) => sum + item.price_excluding_gst,
    0
  );
  const sectionDetailsFormatted: IPanelItem[] = sectionDetails.map((item) => ({
    header: item.line_item_label_name,
    headerTextStyleCfg: { bold: true },
    value: `${item.price_including_gst?.toFixed(2) || "0.00"}`,
    valueTextStyleCfg: { bold: true },
    headerTextId: item.line_item_label_code,
  }));
  return {
    sectionIncludingGstTotal,
    sectionExcludingGstTotal,
    sectionDetailsFormatted,
    hasIncludedItems: sectionDetails.length > 0,
  };
};

export default function PriceBreakdownComponent({
  pricingRecord,
  defaultExpanded,
  showTotalWeeklySubscriptionPrice,
  showSetupFee,
  foQuoting21Enabled,
}: Props) {
  if (!pricingRecord) {
    return null; // or some fallback UI
  }

  const country = pricingRecord.country as Country;
  const currency = countrySpecificCurrency(country);

  // Style for the section
  const style: React.CSSProperties = {
    display: "flex",
    flexDirection: "column",
    gap: "0.5vh",
    marginTop: "1vh",
  };
  return (
    <>
      <Label
        text="Price Breakdown"
        styleCfg={{ bold: true, largeFont: true }}
      />
      <div style={style} data-testid={"priceSummaryPanel"}>
        {showTotalWeeklySubscriptionPrice && (
          <PanelItem
            header={`Total Weekly Subscription Price (${currency})`}
            headerTextStyleCfg={{ bold: true }}
            value={
              (pricingRecord &&
                getTotalWeeklySubscriptionPrice(
                  pricingRecord.pricing_record_line_items
                )?.toFixed(2)) ||
              "0.00"
            }
          />
        )}
        {showSetupFee && (
          <PanelItem
            header={`Setup Fee (${currency})`}
            headerTextStyleCfg={{ bold: true }}
            value={
              pricingRecord?.setup_fee_including_gst?.toFixed(2) ||
              "0.00"
            }
          />
        )}
        {foQuoting21Enabled && pricingRecord?.initial_contribution != null && (
          <PanelItem
            header={`Initial Contribution (${currency})`}
            headerTextStyleCfg={{ bold: true }}
            value={pricingRecord.initial_contribution.toFixed(2)}
          />
        )}
        {foQuoting21Enabled && pricingRecord?.balloon_payment != null && (
          <PanelItem
            header={`Balloon Payment (${currency})`}
            headerTextStyleCfg={{ bold: true }}
            value={pricingRecord.balloon_payment.toFixed(2)}
          />
        )}
      </div>
      <div style={{ margin: "2.5vh 0vw" }}></div>

      {pricingRecord?.pricing_record_sections?.map(
        (section: IPricingRecordSection) => {
          const {
            sectionIncludingGstTotal,
            sectionDetailsFormatted,
            hasIncludedItems,
          } = formatPriceBreakdown(
            pricingRecord,
            section.pricing_template_section_id
          );

          // Only render sections that have included items
          if (!hasIncludedItems) return null;

          return (
            <ExpandablePanelItem
              key={section.pricing_template_section_id}
              header={`${section.section_label} (Total ${currency})`}
              headerTextStyleCfg={{ bold: true }}
              value={`${sectionIncludingGstTotal?.toFixed(2) || "0.00"}`}
              valueTextStyleCfg={{ bold: true }}
              defaultExpanded={defaultExpanded}
            >
              <div
                style={style}
                data-testid={`section${section.section_label}Panel`}
              >
                {sectionDetailsFormatted.map((itemProps, index) => (
                  <PanelItem
                    key={`section${section.section_label}Item-${index}`}
                    {...itemProps}
                    headerTextStyleCfg={{ bold: false, lineBreak: true }}
                    valueTextStyleCfg={{ bold: false }}
                  />
                ))}
              </div>
            </ExpandablePanelItem>
          );
        }
      )}
    </>
  );
}
